import type { CompanyReviewsAISummary, Reviews } from '@seek/libs-shared';
import type { LoaderFunction } from 'react-router';

import type { RouteProps } from '../../../shared/RouteProps';
import type { CompaniesListModel } from '../../models/companiesListModel';

import type { CompanyDetails } from '../../../shared/services/companyDetailsService/types';
import type { AboutModel } from './aboutModel';

const REVIEWS_PER_PAGE = 3;

export const mapAboutModel = ({
  companyDetails,
  randomCompaniesInThisIndustry,
  reviews,
}: {
  companyDetails: CompanyDetails;
  randomCompaniesInThisIndustry?: CompaniesListModel;
  reviews?: Reviews;
}): AboutModel => {
  const companyProfile = companyDetails.companyProfile;
  const companyProfileData = companyProfile?.data;
  const overviewData = companyProfileData?.overview;
  const featuredData = companyProfileData?.featured;
  const missionStatementValue = companyProfileData?.missionStatement; // Renamed to avoid conflict
  const brandingData = companyProfileData?.branding;

  return {
    name: companyProfileData?.name ?? '',
    overview: {
      industry: overviewData?.industry ?? undefined,
      primaryLocation: overviewData?.primaryLocation ?? undefined,
      website: overviewData?.website
        ? {
            url: overviewData.website.url,
            displayName: overviewData.website.displayName as string,
          }
        : undefined,
      description: (overviewData?.description as string[]) ?? [],
      specialities: (overviewData?.specialities as string[]) ?? undefined,
      companySize: overviewData?.size ?? undefined,
      traditionalLand: overviewData?.traditionalLand ?? undefined,
      traditionalPlace: overviewData?.traditionalPlace ?? undefined,
    },
    companyReviewRating: companyProfile?.companyReviewRating ?? undefined,
    featured:
      featuredData &&
      (featuredData.title ||
        featuredData.description ||
        featuredData.imageUrl ||
        featuredData.videoUrl)
        ? {
            description: (featuredData.description as string[]) ?? undefined,
            imageUrl: featuredData.imageUrl ?? undefined,
            title: featuredData.title ?? '',
            videoUrl: featuredData.videoUrl ?? undefined,
          }
        : undefined,
    awards:
      companyProfileData?.awards?.map((award) => ({
        imageUrl: award?.imageUrl || '',
        year: award?.year || 0,
        name: award?.name || '',
      })) ?? undefined,
    missionStatement: missionStatementValue
      ? {
          description: missionStatementValue as string[],
        }
      : undefined,
    gallery: {
      name: 'Gallery',
      photos:
        companyProfileData?.gallery?.map((photo) => ({
          link: photo?.linkUrl ?? '',
          date: photo?.date ?? undefined,
          title: photo?.title ?? undefined,
        })) ?? [],
    },
    branding: brandingData
      ? {
          brandId: brandingData.brandId ?? undefined,
          logoImageUrl: brandingData.logo ?? undefined,
          coverImageUrl: brandingData.cover ?? undefined,
        }
      : undefined,
    randomCompaniesInThisIndustry,
    reviews: reviews ?? undefined,
    companyReviewsAISummary:
      (companyDetails.companyReviewsAISummary as CompanyReviewsAISummary) ??
      undefined,
  };
};

const extractCompanyIdFromSlug = (input?: string): string | undefined => {
  if (!input) {
    return undefined;
  }
  const parts = input.split('-');
  return parts[parts.length - 1];
};

export const aboutLoader =
  ({ services }: RouteProps): LoaderFunction =>
  async ({ params, request }): Promise<AboutModel> => {
    const companyId = extractCompanyIdFromSlug(params.companySlug);

    if (companyId === undefined) {
      throw new Error('Could not find companyId from params', params);
    }
    const url = new URL(request.url);
    const isDraft = url.searchParams.get('draft') === 'true';

    const companyDetails =
      await services.companyDetailsService.getCompanyDetails(
        companyId,
        Boolean(isDraft),
      );

    const formattedDateInUTC = new Date().toISOString().split('T')[0];

    const industry = companyDetails.companyProfile?.data?.overview?.industry;

    // Fetch random companies in the same industry, excluding the current company
    // Always fetch companies even if the industry is not defined, to ensure we have a fallback
    // Random countries are for SEO purposes and to provide a diverse set of companies
    const randomCompaniesInThisIndustry =
      await services.companyProfileService.getGroupedCompanies({
        groupBy: {
          group: 'INDUSTRY',
          value: industry ? [industry] : [],
        },
        perPage: 13,
        excludedCompanyIds: [companyId],
        randomSeed: formattedDateInUTC,
      });

    let reviews;
    if (!isDraft) {
      reviews = await services.companyProfileService.getReviews({
        companyId,
        page: 1,
        sortOrder: 'most recent',
        perPage: REVIEWS_PER_PAGE,
      });
    }
    const result = mapAboutModel({
      companyDetails,
      randomCompaniesInThisIndustry,
      reviews,
    });

    return result;
  };
